import moment from "moment";
import drugHeaders from "./staticFiles/drug_headers.csv";
import LocalizedStrings from "react-localization";
import { DRUG_HEADERS_ID } from "./ERXConstants/localeConstants";
import {
  CUSTOM_BUTTON_STATUS,
  DRUG_QUANTITY_SYMBOLS,
} from "./ERXConstants/constants";

// pass age and DOB. Convert this into years month and days
export const getAgeInYearsMonthDays = (dob, age) => {
  let currentAge = "";
  if (dob) {
    let startDate = moment(dob, "YYYY-MM-DD");
    let endDate = moment();
    const duration = moment.duration(endDate.diff(startDate));
    currentAge =
      `${duration.years() > 0 ? duration.years() + " years " : " 00 years "}` +
      `${
        duration.months() > 0 ? duration.months() + " months " : " 00 months "
      }` +
      `${duration.days() > 0 ? duration.days() + " days " : " 00 days "}`;
  } else if (age) {
    currentAge = age;
  }
  return currentAge;
};

// helper function to read the drug headers csv file from static folders
// convert it into the structure required for localization.
// fetch the drug headers based on the language provided
export const getDrugHeaders = async (lang) => {
  let result = await fetch(drugHeaders, { mode: "no-cors" }).then(
    async (response) => {
      let res = await response.text();
      let array = res?.split("\r\n");
      let headers = array[0].split(",");
      let obj = {};

      array.forEach((ele, index) => {
        if (ele.trim() && index !== 0) {
          let str = ele.split(",");
          if (str.length !== 0) {
            headers.forEach((head, ind) => {
              obj[head] = { ...obj[head], [str[0]]: str[ind] };
            });
          }
        }
      });
      return obj;
    }
  );

  let strings = new LocalizedStrings(result);
  if (lang?.trim()) {
    strings.setLanguage(lang);
  }
  return {
    [DRUG_HEADERS_ID.CUSTOM_DRUG_HEADER_NAME]: strings.formatString(
      strings["Drug Name"]
    ),
    [DRUG_HEADERS_ID.CUSTOM_DRUG_HEADER_FREQUENCY]: strings.formatString(
      strings["Frequency"]
    ),
    [DRUG_HEADERS_ID.CUSTOM_DRUG_HEADER_DURATION]: strings.formatString(
      strings["Period"]
    ),
    [DRUG_HEADERS_ID.CUSTOM_DRUG_HEADER_INSTRUCTIONS]: strings.formatString(
      strings["Instructions"]
    ),
  };
};

// function takes vitals list and gender and returns the final list after doing all kinds of null check
export const getFinalVitalList = (vitalsList, updatedGender) => {
  let list = [];
  vitalsList.forEach((ele) => {
    if (
      ele.value?.toString() &&
      ele.value?.toString()?.toLowerCase() !== "nan" &&
      ele.value?.toString()?.toLowerCase() !== "infinity"
    ) {
      if (ele.name.toLowerCase() === "lmp") {
        if (updatedGender?.toLowerCase() === "female") {
          list.push(ele);
        }
      } else {
        list.push(ele);
      }
    }
  });

  return list;
};

// function takes patient history list and gender and returns the final list after doing all kinds of null check
export const getFinalPatientHistoryList = (
  patientHistory,
  obstetricHistoryList,
  updatedGender,
  erxPatientHistoryComponent
) => {
  let list = [];
  let historySettings = ["chief_complaints"];
  erxPatientHistoryComponent.settings.forEach((data) => {
    if (data?.answer) {
      historySettings.push(data.que_key);
    }
  });
  patientHistory.forEach((ele) => {
    if (historySettings?.includes(ele?.name) && ele.value?.length !== 0) {
      if (ele.name === "menstrual_history") {
        if (updatedGender?.toLowerCase() === "female") {
          let arr = ele.value.filter((item) => item?.title?.trim());
          if (arr.length !== 0) {
            list.push({ ...ele, value: arr });
          }
        }
      } else if (ele.name === "obstetric_history") {
        if (updatedGender?.toLowerCase() === "female") {
          let arr = obstetricHistoryList.filter((item) => item?.value?.trim());
          if (arr.length !== 0) {
            list.push({ ...ele, value: arr });
          }
        }
      } else {
        let arr = ele.value.filter((item) => item?.title?.trim());
        if (arr.length !== 0) {
          list.push({ ...ele, value: arr });
        }
      }
    }
  });

  return list;
};

// function takes diagnosis list and returns the final list after doing all kinds of null check
export const getFinalDiagnosisList = (diagnosisList) => {
  return diagnosisList?.filter(
    (ele) => ele.title?.trim() !== "" || ele.description?.trim() !== ""
  );
};

// function takes diagnosis list and returns the final list after doing all kinds of null check
export const getFinalReportList = (reportList) => {
  return reportList?.filter(
    (ele) => ele.title?.trim() !== "" || ele.description?.trim() !== ""
  );
};

// function takes drug list and returns the final list after doing all kinds of null check
export const getFinalDrugsList = (drugList) => {
  let list = [];
  if (drugList) {
    drugList.forEach((item) => {
      if (
        (item.drug?.trim() ||
          item.frequency_extended?.title?.trim() ||
          item.frequency_exyended?.details?.trim() ||
          item.duration?.trim() ||
          item.instructions?.trim()) &&
        item.deleted_at?.toLowerCase() !== "now()" &&
        item?.id
      ) {
        list.push(item);
      }
    });
  }

  return list;
};

// function takes vaccine list and returns the final list after doing all kinds of null check
export const getFinalVaccineList = (vaccineChart) => {
  let list = [];
  Object.keys(vaccineChart).forEach((ele) => {
    vaccineChart[ele].forEach((ele1) => {
      if (ele1.scheduled_date?.trim()) {
        list.push(ele1);
      }
    });
  });

  return list;
};

// function takes suggested investigations and instructions list and returns the final list after doing all kinds of null check
export const getFinalInvestigationsAndInstructionsList = (
  investigationsInstructionsList
) => {
  let list = [];
  investigationsInstructionsList.forEach((ele) => {
    if (ele.value?.length !== 0) {
      let arr = ele.value.filter((item) => item?.title?.trim());
      if (arr.length !== 0) {
        list.push({ ...ele, value: arr });
      }
    }
  });

  return list;
};

// function takes patient history list and returns the final list of only chief complaints after doing all kinds of null check
export const getFinalChiefComplaintsList = (patientHistory) => {
  let list = [];
  patientHistory.forEach((ele) => {
    if (ele.name === "chief_complaints" && ele.value?.length !== 0) {
      let arr = ele.value.filter((item) => item?.title?.trim());
      if (arr.length !== 0) {
        list.push({ ...ele, value: arr });
      }
    }
  });

  return list;
};

// function takes patient history list and returns the final list except chief complaints after doing all kinds of null check
export const getFinalMedicalHistoryList = (patientHistory, updatedGender) => {
  let list = [];
  patientHistory.forEach((ele) => {
    if (
      ele.name !== "chief_complaints" &&
      ele.name !== "obstetric_history" &&
      ele.value?.length !== 0
    ) {
      if (ele.name === "menstrual_history") {
        if (updatedGender?.toLowerCase() === "female") {
          let arr = ele.value.filter((item) => item?.title?.trim());
          if (arr.length !== 0) {
            list.push({ ...ele, value: arr });
          }
        }
      } else {
        let arr = ele.value.filter((item) => item?.title?.trim());
        if (arr.length !== 0) {
          list.push({ ...ele, value: arr });
        }
      }
    }
  });

  return list;
};

// function takes suggested investigations and instructions list and returns the final list of only suggested investigations after doing all kinds of null check
export const getFinalSuggestedInvestigationsList = (
  investigationsInstructionsList
) => {
  let list = [];
  investigationsInstructionsList.forEach((ele) => {
    if (ele?.name === "suggested_investigation" && ele.value?.length !== 0) {
      let arr = ele.value.filter((item) => item?.title?.trim());
      if (arr.length !== 0) {
        list.push({ ...ele, value: arr });
      }
    }
  });

  return list;
};

// function takes suggested investigations and instructions list and returns the final list of only instructions and notes after doing all kinds of null check
export const getFinalInstructionsList = (investigationsInstructionsList) => {
  let list = [];
  investigationsInstructionsList.forEach((ele) => {
    if (ele?.name === "instructions_and_notes" && ele.value?.length !== 0) {
      let arr = ele.value.filter((item) => item?.title?.trim());
      if (arr.length !== 0) {
        list.push({ ...ele, value: arr });
      }
    }
  });

  return list;
};

// function takes obstetric history list and returns the final list after doing all kinds of null check
export const getFinalObstetricHistoryList = (obstetricHistoryList) => {
  return obstetricHistoryList.filter((item) => item?.value?.trim());
};

// function takes custom section list and returns the final list after doing all kinds of null check
export const getFinalCustomSectionList = (customSectionList) => {
  customSectionList.fields.forEach((fields) => {
    fields.field_title = fields.field_title?.trim();
    fields.value.forEach((value) => {
      value.title = value.title?.trim();
    });
  });

  return customSectionList.fields?.filter(
    (fields) =>
      fields.field_title?.trim() ||
      fields.value?.filter((value) => value.title?.trim())?.length !== 0
  );
};

// function takes obstetric calculator details and returns the final data after doing all kinds of null check
export const getFinalObstetricCalculatorDetails = (obstetricDetails) => {
  let list = [];

  if (obstetricDetails?.lmpDate?.toString()?.trim()) {
    list.push({ title: "LMP", value: [{ title: obstetricDetails?.lmpDate }] });
  }
  if (obstetricDetails?.eddDate?.toString()?.trim()) {
    list.push({ title: "EDD", value: [{ title: obstetricDetails?.eddDate }] });
  }

  let egaValues = [];
  if (
    (obstetricDetails?.egaWeeks?.toString()?.trim() ||
      +obstetricDetails?.egaWeeks === 0) &&
    obstetricDetails?.lmpDate?.toString()?.trim()
  ) {
    egaValues.push({ title: obstetricDetails?.egaWeeks + " Weeks" });
  }
  if (
    (obstetricDetails?.egaDays?.toString()?.trim() ||
      +obstetricDetails?.egaDays === 0) &&
    obstetricDetails?.lmpDate?.toString()?.trim()
  ) {
    egaValues.push({ title: obstetricDetails?.egaDays + " Days" });
  }
  if (egaValues?.length !== 0) {
    list.push({ title: "EGA", value: egaValues });
  }

  if (obstetricDetails?.note?.trim()) {
    list.push({ title: "Note", value: obstetricDetails?.note?.trim() });
  }

  return list;
};

export const getDrugDropDownOptions = (res) => {
  let frequencyArr = [],
    durationArr = [],
    detailsArr = [],
    quantityArr = [],
    unitArr = [];
  if (res?.freq && res?.freq?.length > 0) {
    res.freq.forEach((data) => {
      let freqObj = {};
      freqObj["label"] = data;
      freqObj["value"] = data;
      freqObj["selected"] = false;
      frequencyArr.push(freqObj);
    });
  }
  if (res?.freq_detail_rev && res?.freq_detail_rev?.length > 0) {
    res.freq_detail_rev.forEach((data) => {
      let detailObj = { ...data };
      detailObj["label"] = data?.title;
      detailObj["value"] = data?.title;
      detailObj["selected"] = false;
      detailsArr.push(detailObj);
    });
  }
  if (res?.durations_rev && res?.durations_rev?.length > 0) {
    res.durations_rev.forEach((data) => {
      let durationObj = { ...data };
      durationObj["label"] = data?.title;
      durationObj["value"] = data?.title;
      durationObj["selected"] = false;
      durationArr.push(durationObj);
    });
    durationArr.push({
      label: "Custom",
      value: "Custom",
      selected: false,
    });
  }
  if (res?.default_quantities && res?.default_quantities?.length > 0) {
    res.default_quantities.forEach((data) => {
      let quantityObj = {};
      let listValue = getFraction(data);
      quantityObj["label"] = listValue;
      quantityObj["value"] = listValue;
      quantityObj["selected"] = false;
      quantityArr.push(quantityObj);
    });
    quantityArr.push({
      label: "Custom",
      value: "Custom",
      selected: false,
    });
  }
  if (res?.default_units && res?.default_units?.length > 0) {
    res.default_units.forEach((data) => {
      let unitObj = {};
      unitObj["label"] = data;
      unitObj["value"] = data;
      unitObj["selected"] = false;
      unitArr.push(unitObj);
    });
    unitArr.push({ label: "Custom", value: "Custom", selected: false });
  }
  return {
    frequencyArr: frequencyArr,
    durationArr: durationArr,
    detailsArr: detailsArr,
    quantityArr: quantityArr,
    unitArr: unitArr,
  };
};

const getFraction = (data) => {
  let decimal = (data % 1).toFixed(2);
  let integer = data - decimal;
  if (+decimal !== 0) {
    integer = integer > 0 ? integer : "";
    let fraction = DRUG_QUANTITY_SYMBOLS.hasOwnProperty(decimal)
      ? DRUG_QUANTITY_SYMBOLS[decimal]
      : "";
    return integer + fraction;
  } else {
    return data;
  }
};

export const saveDrugDataHelper = (drugData, prescription_id) => {
  let arr = [];
  drugData.forEach((data) => {
    let obj = {};
    obj["id"] = data?.id;
    obj["appointment_prescription_id"] = prescription_id
      ? prescription_id
      : data?.appointment_prescription_id;
    obj["drug"] = data?.drug;
    obj["frequency"] =
      data?.frequency_extended?.title + "," + data?.frequency_extended?.details;
    if (data?.duration.includes("undefined")) {
      data["duration"] = data?.duration?.replace("undefined", "Days");
    }
    obj["duration"] = data?.duration;
    obj["instructions"] = data?.instructions;
    obj["drug_master_id"] = data?.drug_master_id ? data?.drug_master_id : null;
    obj["frequency_extended"] = {
      title: data?.frequency_extended?.title,
      details: data?.frequency_extended?.details,
      slug: data?.frequency_extended?.details
        ? data?.frequency_extended?.details
        : null,
    };
    obj["deleted_at"] = data?.deleted_at ?? null;

    obj["knowledge_base_drug_id"] = data?.knowledge_base_drug_id;
    obj["duration_slug"] = data?.duration_slug;
    obj["instructions_slug"] = data?.instructions_slug;
    obj["frequency_slug"] = data?.frequency_slug;
    obj["drug_button_status"] =
      data?.knowledge_base_drug_id ||
      data?.drug_master_id ||
      data?.drug?.trim() === ""
        ? ""
        : CUSTOM_BUTTON_STATUS.SAVE;
    obj["instruction_button_status"] =
      data?.knowledge_base_id ||
      data?.master_id ||
      data?.instructions?.trim() === ""
        ? ""
        : CUSTOM_BUTTON_STATUS.SAVE;
    obj["quantity"] = data?.quantity;
    obj["unit"] = data?.unit;
    if (data?.knowledge_base_id) {
      obj["knowledge_base_id"] = data?.knowledge_base_id;
    } else if (data?.master_id) {
      obj["master_id"] = data?.master_id;
    }
    obj["short_name"] = data?.short_name ?? "";
    arr.push(obj);
  });
  return arr;
};

export const getPayloadForDrug = (addedDeletedDrugList, prescription_id) => {
  let modifyPayload = [];
  addedDeletedDrugList.forEach((data) => {
    let obj = {};
    if (data?.id) {
      obj["id"] = data?.id;
    }
    obj["appointment_prescription_id"] = prescription_id;
    obj["drug"] = data?.drug?.trim();
    obj["frequency"] =
      data?.frequency_extended?.title + "," + data?.frequency_extended?.details;
    obj["duration"] = data?.duration?.trim();
    obj["instructions"] = data?.instructions?.trim();
    obj["drug_master_id"] = data?.drug_master_id ? data?.drug_master_id : null;
    obj["frequency_extended"] = {
      title: data?.frequency_extended?.title?.trim(),
      details: data?.frequency_extended?.details,
      slug: data?.frequency_slug ? data?.frequency_slug : null,
    };
    obj["deleted_at"] = data?.deleted_at ? data?.deleted_at : null;
    obj["knowledge_base_drug_id"] =
      data?.knowledge_base_drug_id && data?.knowledge_base_drug_id !== ""
        ? data?.knowledge_base_drug_id
        : null;
    obj["instructions_slug"] = data?.instructions_slug
      ? data?.instructions_slug
      : data?.instructions?.trim();
    obj["duration_slug"] = data?.duration_slug ? data?.duration_slug : null;
    obj["frequency_slug"] = data?.frequency_slug ? data?.frequency_slug : null;
    if (data?.knowledge_base_id) {
      obj["knowledge_base_drug_instruction_id"] = data?.knowledge_base_id;
    }
    obj["quantity"] = data?.quantity?.trim();
    obj["unit"] = data?.unit?.trim();
    modifyPayload.push(obj);
  });

  return modifyPayload;
};

export const getHistoryAndComplaintPayload = (key, data) => {
  let index = data.findIndex((val) => val.name === key);
  return data[index].value;
};

export const getObstetricHistoryPayload = (obstetricHistoryList) => {
  let obj = {};
  obstetricHistoryList.value.forEach((item) => {
    obj[item.label] = item.value;
  });
  return obj;
};

export const getSuggestedInvestigationPayload = (key, data) => {
  let index = data.findIndex((val) => val.name === key);
  return data[index].value;
};

export const getInstructionAndNotesPayload = (key, data) => {
  let index = data.findIndex((val) => val.name === key);
  return data[index].value;
};

export const getCustomSectionsValue = (customSectionList) => {
  let customSectionPayload = [];
  if (Object.keys(customSectionList)?.length > 0) {
    Object.values(customSectionList).forEach((data) => {
      customSectionPayload.push(data);
    });
  } else {
    customSectionPayload = [];
  }
  return customSectionPayload;
};

export const getDueDates = (item, splitBy, addBy, patientDob) => {
  let data = item.title?.toLowerCase().split(splitBy)[0]?.trim();
  if (item.title?.includes("-")) {
    data = item.title?.toLowerCase().split("-")[0]?.trim();
  }
  item.vaccines.forEach((ele) => {
    ele.scheduled_date = ele.scheduled_date
      ? ele.scheduled_date
      : moment(patientDob).add(data, addBy).format("DD MMM YYYY");
    ele.due_date_for_vitals = moment(patientDob)
      .add(data, addBy)
      .format("DD MMM YYYY");
  });
  return item;
};
